import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, InternetIcon } from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { InputField } from '@components/Input';
import { SaaeLogo } from '@components/SaaeLogo';
import { otpFormSchema, type OtpFormValues } from '@schemas/formSchemas';
import { useAuth } from '@contexts/AuthContext';
import { authVerifyOtp, authLogout, API_ERROR_CLIENT_PHONE_NOT_ON_DEAL, type AuthOtpContext } from '@services/api';
import type { ApiError, AuthVerifyOtpData } from '@services/api';
import { useLanguage } from '@hooks/useLanguage';
import { RtlBackIcon } from '@utils/rtlIcons';
import {
  clearSignupOtpSession,
  formatPhoneForOtpDisplay,
  isSignupOtpSessionExpired,
  loadSignupOtpSession,
  saveSignupOtpSession,
  signupOtpSessionMatchesRoute,
} from '@utils/signupOtpSession';
import heroImage from '../public/images/hero-image.webp';

export interface SignUpOtpPageProps {
  onBack?: () => void;
  /** Called when OTP session is missing, expired (>3 min), or mismatched; parent should redirect to phone step. */
  onSessionExpired?: () => void;
  /** Called after successful OTP verification; receives auth data so caller can route by profile completeness. */
  onVerify?: (authData: AuthVerifyOtpData) => void;
  phoneNumber?: string;
  onResendCode?: () => void;
  /** When false, only the left panel is rendered (hero is static in parent). */
  showRightPanel?: boolean;
  /** When true, render only the center content (no layout/header/footer); used inside AuthFlowShell. */
  contentOnly?: boolean;
  authContext?: AuthOtpContext;
  /** Payer flow: deal context for strict phone validation */
  clientDealId?: string;
  /** Payer flow: payment token from link query */
  clientToken?: string;
}

export function SignUpOtpPage({
  onBack,
  onSessionExpired,
  onVerify,
  phoneNumber,
  onResendCode,
  showRightPanel = true,
  contentOnly = false,
  authContext = 'broker',
  clientDealId,
  clientToken,
}: SignUpOtpPageProps) {
  const { t } = useTranslation();
  const { setSessionToken, setAuthenticated, clearSession, refreshAuth } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const languageLabel = language === 'ar' ? t('common.english') : t('common.arabic');
  const [verificationPhone, setVerificationPhone] = useState<string | null>(null);
  /** pending: resolving session; ready: show OTP form; expired: redirecting to phone step */
  const [otpGate, setOtpGate] = useState<'pending' | 'ready' | 'expired'>('pending');
  const expiredNotifiedRef = useRef(false);
  const displayPhone = verificationPhone ? formatPhoneForOtpDisplay(verificationPhone) : '';
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    expiredNotifiedRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset OTP gate state on mount / dependency change
    setOtpGate('pending');
    setVerificationPhone(null);

    const notifyExpired = () => {
      if (cancelled || expiredNotifiedRef.current) return;
      expiredNotifiedRef.current = true;
      clearSignupOtpSession();
      onSessionExpired?.();
    };

    const session = loadSignupOtpSession();

    if (session) {
      if (isSignupOtpSessionExpired(session)) {
        clearSignupOtpSession();
        notifyExpired();
        if (!cancelled) setOtpGate('expired');
        return () => {
          cancelled = true;
        };
      }
      if (!signupOtpSessionMatchesRoute(session, authContext, clientDealId, clientToken)) {
        clearSignupOtpSession();
        notifyExpired();
        if (!cancelled) setOtpGate('expired');
        return () => {
          cancelled = true;
        };
      }
      if (!cancelled) {
        setVerificationPhone(session.phone);
        setOtpGate('ready');
      }
      return () => {
        cancelled = true;
      };
    }

    const propPhone = phoneNumber?.trim() ?? '';
    if (propPhone) {
      saveSignupOtpSession({
        phone: propPhone,
        authContext,
        clientDealId,
        clientToken,
      });
      if (!cancelled) {
        setVerificationPhone(propPhone);
        setOtpGate('ready');
      }
      return () => {
        cancelled = true;
      };
    }

    notifyExpired();
    if (!cancelled) setOtpGate('expired');
    return () => {
      cancelled = true;
    };
  }, [phoneNumber, authContext, clientDealId, clientToken, onSessionExpired]);

  const { control, handleSubmit, formState: { errors, dirtyFields } } = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { code: '' },
    mode: 'onChange',
  });
  const codeDirty = dirtyFields.code;
  const codeError = errors.code?.message;
  const codeSuccess = codeDirty && !codeError;

  const onOtpSubmit = async (data: OtpFormValues) => {
    if (!verificationPhone) return;
    setApiError(null);
    setLoading(true);
    try {
      const authData = await authVerifyOtp(
        verificationPhone,
        data.code,
        authContext === 'client' ? { authContext: 'client', dealId: clientDealId, token: clientToken } : undefined,
      );
      // Payer/client flow: HttpOnly cookie is set by verify-otp; sync context from GET /auth/me.
      if (authContext !== 'client') {
        if (authData.token) {
          setSessionToken(authData.token, true, { profileCompleted: authData.profileCompleted });
        } else {
          setAuthenticated(true);
        }
      } else {
        await refreshAuth();
      }
      clearSignupOtpSession();
      onVerify?.(authData);
    } catch (err) {
      const e = err as ApiError;
      if (authContext === 'client' && e.code === API_ERROR_CLIENT_PHONE_NOT_ON_DEAL) {
        setApiError(t('payer.errors.phoneNotOnDeal'));
      } else {
        setApiError(e.message ?? t('auth.otp.invalidCode', { defaultValue: 'رمز غير صالح أو منتهي. حاول مرة أخرى.' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    clearSignupOtpSession();
    if (authContext !== 'client') {
      void authLogout()
        .catch(() => {})
        .finally(() => {
          clearSession();
          onBack?.();
        });
      return;
    }
    onBack?.();
  };

  if (otpGate === 'expired') {
    return null;
  }

  if (otpGate !== 'ready' || !verificationPhone) {
    const loadingBlock = (
      <div className="flex min-h-[200px] w-full max-w-md flex-col items-center justify-center gap-2">
        <span className="font-sans text-base text-gray-600">{t('common.loading', { defaultValue: 'Loading…' })}</span>
      </div>
    );
    return contentOnly ? loadingBlock : (
      <div className="min-h-screen bg-surface-web flex flex-col items-center justify-center p-0 box-border font-sans">
        <div className="w-full max-w-7xl mx-auto flex flex-col items-center justify-center gap-6 ps-8 pe-8 box-border min-h-screen py-6 max-[767px]:py-6 max-[767px]:ps-4 max-[767px]:pe-4">
          <div className="flex w-full max-w-2xl flex-1 flex-col">{loadingBlock}</div>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="w-full max-w-md flex flex-col items-center gap-4 max-[767px]:w-[calc(100%-3rem)] max-[767px]:max-w-md">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex flex-col items-center gap-4">
          <SaaeLogo size="large" mode="light" className="!h-[39px] w-auto" />
          <h1 className="font-sans text-2xl font-semibold text-primary m-0 max-w-md max-[767px]:text-xl leading-tight">
            {t('auth.otp.headline')}
          </h1>
        </div>
        <p className="font-sans text-base font-normal text-gray-600 m-0 pt-4 max-w-sm leading-relaxed">
          {t('auth.otp.codeSentTo')}
          <br />
          <strong dir="ltr" className="inline">
            {displayPhone}
          </strong>
        </p>
      </div>
      <form onSubmit={handleSubmit(onOtpSubmit)} className="w-full flex flex-col items-center gap-4" noValidate>
        <div className="flex flex-col gap-2 items-start w-full [&_.max-w-xs]:max-w-none">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <InputField
                name={field.name}
                size="lg"
                label={t('auth.otp.verificationCode')}
                type="text"
                placeholder={t('auth.otp.placeholder')}
                inputMode="numeric"
                maxLength={6}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                state={codeError ? 'error' : codeSuccess ? 'success' : 'default'}
                helperText={codeError ? t(codeError) : undefined}
                showHelperIcon={true}
                fullWidth
              />
            )}
          />
        </div>
        {apiError && (
          <p className="text-sm text-surface-error-primary font-sans" role="alert">{apiError}</p>
        )}
        <p className="font-sans text-base font-normal text-gray-600 m-0 self-start">
          {t('auth.otp.didNotReceive')}{' '}
          <button
            type="button"
            className="font-sans text-base font-normal text-secondary bg-transparent border-none p-0 cursor-pointer underline hover:opacity-90"
            onClick={onResendCode}
          >
            {t('auth.otp.sendAgain')}
          </button>
        </p>
        <Button type="submit" variant="primary" size="lg" fullWidth className="h-14 max-w-md rounded-[16px]" disabled={loading}>
          {loading ? t('common.loading', { defaultValue: 'جاري التحقق…' }) : t('auth.otp.next')}
        </Button>
      </form>
    </div>
  );

  if (contentOnly) return inner;

  return (
    <div className="min-h-screen bg-surface-web flex flex-col items-center justify-center p-0 box-border font-sans">
      <div className={`w-full max-w-7xl mx-auto flex flex-col items-center justify-center gap-6 ps-8 pe-8 box-border min-h-screen py-6 max-[767px]:py-6 max-[767px]:ps-4 max-[767px]:pe-4 ${showRightPanel ? 'lg:flex-row lg:items-start lg:py-6' : ''}`}>
        <div className="flex w-full max-w-2xl min-h-0 flex-1 flex-col rounded-2xl overflow-visible border-none bg-transparent lg:w-[646px] lg:min-h-[520px] lg:self-stretch max-[767px]:min-h-[400px] max-[767px]:w-full max-[767px]:max-w-2xl">
          <div className="flex shrink-0 w-full items-center justify-between gap-2 py-2">
            {onBack ? (
              <Button
                variant="ghost"
                size="md"
                className="h-14 p-4 rounded-[16px]"
                onClick={handleBack}
                icon={<RtlBackIcon><HugeiconsIcon icon={ArrowLeft01Icon} size={20} /></RtlBackIcon>}
                iconPosition="left"
              >
                {t('common.back')}
              </Button>
            ) : (
              <span />
            )}
            <Button
              variant="ghost"
              size="md"
              className="h-14 p-4 rounded-[16px]"
              icon={<HugeiconsIcon icon={InternetIcon} size={20} />}
              iconPosition="right"
              aria-label={languageLabel}
              onClick={toggleLanguage}
            >
              {languageLabel}
            </Button>
          </div>
          <div className="mt-6 flex flex-1 min-h-0 w-full items-center justify-center px-0">
            {inner}
          </div>
          <footer className="shrink-0 py-2 text-center font-sans text-base font-normal leading-snug text-gray-900">
            {t('auth.footer')}
          </footer>
        </div>
        {showRightPanel && (
          <div className="hidden w-[646px] min-h-[520px] shrink-0 rounded-2xl overflow-hidden lg:block lg:self-stretch xl:min-w-0 xl:flex-1" aria-hidden>
            <img
              src={heroImage}
              alt=""
              width={646}
              height={520}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-center block"
              aria-hidden
            />
          </div>
        )}
      </div>
    </div>
  );
}
